import { useMemo, useState } from "react";
import { Filter, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/UI/Popover";
import { useContacts } from "../../contexts/ContactsContext";
import { cn } from "../../../../utils/utils";

/**
 * Popover filter nâng cao — chọn Giai đoạn bán hàng + Tag cài đặt + Nhân viên.
 * Dữ liệu lookup lấy từ context.lookups (populated bởi ContactsLayout).
 */
const ContactsAdvancedFilterButton = () => {
  const { filters, setFilters, lookups, selectedShopIds } = useContacts();
  const [open, setOpen] = useState(false);

  const pipelineStages = useMemo(
    () => Object.values(lookups.pipelineStagesById || {}),
    [lookups.pipelineStagesById],
  );
  const tags = useMemo(() => Object.values(lookups.tagsById || {}), [
    lookups.tagsById,
  ]);
  const staffs = useMemo(
    () => Object.values(lookups.staffsById || {}),
    [lookups.staffsById],
  );

  const activeCount =
    (filters.pipelineStageIds?.length || 0) +
    (filters.tagIds?.length || 0) +
    (filters.staffIds?.length || 0);

  const toggle = (key, value) => {
    setFilters((prev) => {
      const arr = prev[key] || [];
      const exists = arr.includes(value);
      return {
        ...prev,
        [key]: exists ? arr.filter((x) => x !== value) : [...arr, value],
      };
    });
  };

  const clearAll = () => {
    setFilters((prev) => ({
      ...prev,
      pipelineStageIds: [],
      tagIds: [],
      staffIds: [],
    }));
  };

  const disabled = selectedShopIds.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          className={cn(
            "h-8 px-2.5 inline-flex items-center gap-1.5 rounded-md border text-[13px] transition-colors",
            activeCount > 0
              ? "border-indigo-300 bg-indigo-50 text-indigo-700"
              : "border-gray-200 text-gray-700 hover:bg-gray-50",
            disabled && "opacity-60 cursor-not-allowed",
          )}
        >
          <Filter size={13} />
          <span>Bộ lọc</span>
          {activeCount > 0 && (
            <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 text-[10px] px-1 rounded-full bg-indigo-600 text-white">
              {activeCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 rounded-xl shadow-lg border border-gray-100"
        align="end"
        sideOffset={6}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
          <div className="text-sm font-semibold text-gray-900">Bộ lọc</div>
          {activeCount > 0 && (
            <button
              onClick={clearAll}
              className="text-[11px] text-gray-500 hover:text-red-600 inline-flex items-center gap-1"
            >
              <X size={12} /> Xoá tất cả
            </button>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
          {/* Pipeline stages */}
          <div className="p-3">
            <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2">
              Giai đoạn bán hàng
            </div>
            {pipelineStages.length === 0 ? (
              <div className="text-[12px] text-gray-400">Chưa cấu hình</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {pipelineStages.map((s) => {
                  const active = filters.pipelineStageIds?.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggle("pipelineStageIds", s.id)}
                      className={cn(
                        "px-2 py-1 text-[12px] rounded-full border",
                        active
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-gray-700 border-gray-200 hover:border-gray-300",
                      )}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tag cài đặt */}
          <div className="p-3">
            <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2">
              Tag cài đặt
            </div>
            {tags.length === 0 ? (
              <div className="text-[12px] text-gray-400">Chưa có tag</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((t) => {
                  const active = filters.tagIds?.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => toggle("tagIds", t.id)}
                      className={cn(
                        "px-2 py-1 text-[12px] rounded-full border inline-flex items-center gap-1",
                        active
                          ? "text-white border-transparent"
                          : "bg-white text-gray-700 border-gray-200 hover:border-gray-300",
                      )}
                      style={
                        active && t.color ? { backgroundColor: t.color } : {}
                      }
                    >
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: t.color || "#9ca3af" }}
                      />
                      {t.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Nhân viên */}
          {staffs.length > 0 && (
            <div className="p-3">
              <div className="text-[11px] font-semibold text-gray-500 uppercase mb-2">
                Nhân viên phụ trách
              </div>
              <div className="flex flex-wrap gap-1.5">
                {staffs.slice(0, 30).map((s) => {
                  const active = filters.staffIds?.includes(s._id || s.id);
                  return (
                    <button
                      key={s._id || s.id}
                      onClick={() => toggle("staffIds", s._id || s.id)}
                      className={cn(
                        "px-2 py-1 text-[12px] rounded-full border",
                        active
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-gray-700 border-gray-200 hover:border-gray-300",
                      )}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ContactsAdvancedFilterButton;
